import { useState } from 'react';
import { useTransferencias } from '@/features/financeiro/hooks/useTransferencias';
import type { RealizarTransferenciaRequest } from '@/features/financeiro/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ArrowRightLeft, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

function fmt(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtDate(s: string) {
  return format(new Date(s), 'dd/MM/yyyy HH:mm', { locale: ptBR });
}

// ── Dialog de Nova Transferência ──────────────────────────────────────────

interface TransferenciaDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (req: RealizarTransferenciaRequest) => Promise<void>;
  isLoading: boolean;
}

function TransferenciaDialog({ open, onClose, onConfirm, isLoading }: TransferenciaDialogProps) {
  const [origemId,  setOrigemId]  = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [valor,     setValor]     = useState('');
  const [descricao, setDescricao] = useState('');
  const [historico, setHistorico] = useState('');
  const [dtMov,     setDtMov]     = useState(format(new Date(), 'yyyy-MM-dd'));

  const reset = () => {
    setOrigemId(''); setDestinoId(''); setValor(''); setDescricao(''); setHistorico('');
    setDtMov(format(new Date(), 'yyyy-MM-dd'));
  };

  const handleConfirm = async () => {
    if (!origemId || !destinoId || !valor || !descricao) {
      toast.error('Preencha todos os campos obrigatórios.');
      return;
    }
    if (origemId === destinoId) {
      toast.error('Origem e destino não podem ser iguais.');
      return;
    }
    if (parseFloat(valor) <= 0) {
      toast.error('O valor deve ser maior que zero.');
      return;
    }
    await onConfirm({
      caixaOrigemId:  parseInt(origemId),
      caixaDestinoId: parseInt(destinoId),
      valor:          parseFloat(valor),
      descricao,
      dtMovimento:    dtMov,
      historico:      historico || undefined,
    });
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Nova Transferência
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Caixa Origem *</Label>
              <Input
                type="number"
                value={origemId}
                onChange={e => setOrigemId(e.target.value)}
                placeholder="ID do caixa"
              />
            </div>
            <div className="space-y-1">
              <Label>Caixa Destino *</Label>
              <Input
                type="number"
                value={destinoId}
                onChange={e => setDestinoId(e.target.value)}
                placeholder="ID do caixa"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Valor (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                value={valor}
                onChange={e => setValor(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div className="space-y-1">
              <Label>Data</Label>
              <Input
                type="date"
                value={dtMov}
                onChange={e => setDtMov(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Descrição *</Label>
            <Input
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="Ex: Repasse para caixa 2"
            />
          </div>

          <div className="space-y-1">
            <Label>Histórico</Label>
            <Input
              value={historico}
              onChange={e => setHistorico(e.target.value)}
              placeholder="Informação complementar (opcional)"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Processando...' : 'Realizar Transferência'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Página Principal ──────────────────────────────────────────────────────

export default function Transferencias() {
  const hoje    = format(new Date(), 'yyyy-MM-dd');
  const inicioMes = format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd');

  const [dtInicio, setDtInicio] = useState(inicioMes);
  const [dtFim,    setDtFim]    = useState(hoje);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { transferencias, isLoading, realizar, isRealizando } =
    useTransferencias(dtInicio, dtFim);

  // Agrupa por par origem→destino para exibir débitos e créditos juntos
  const debitos  = transferencias.filter(t => t.debitoCredito === 'D');
  const creditos = transferencias.filter(t => t.debitoCredito === 'C');

  const handleRealizar = async (req: RealizarTransferenciaRequest) => {
    try {
      const res = await realizar(req);
      toast.success(res.mensagem);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { mensagem?: string } } })?.response?.data?.mensagem;
      toast.error(msg ?? 'Erro ao realizar transferência.');
    }
  };

  // KPIs
  const totalTransferido = debitos.reduce((s, t) => s + t.valor, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Transferências entre Caixas</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Movimentação de valores entre caixas operacionais
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Transferência
        </Button>
      </div>

      {/* Filtro de Período */}
      <div className="flex gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">De</Label>
          <Input type="date" value={dtInicio} onChange={e => setDtInicio(e.target.value)} className="w-38" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">até</Label>
          <Input type="date" value={dtFim} onChange={e => setDtFim(e.target.value)} className="w-38" />
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border p-4 space-y-1">
          <p className="text-xs text-muted-foreground">Total Transferido</p>
          <p className="text-2xl font-semibold">{fmt(totalTransferido)}</p>
        </div>
        <div className="rounded-lg border p-4 space-y-1">
          <p className="text-xs text-muted-foreground">Operações</p>
          <p className="text-2xl font-semibold">{debitos.length}</p>
        </div>
        <div className="rounded-lg border p-4 space-y-1">
          <p className="text-xs text-muted-foreground">Período</p>
          <p className="text-sm font-medium">{dtInicio} → {dtFim}</p>
        </div>
      </div>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>D/C</TableHead>
              <TableHead>Caixa</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Histórico</TableHead>
              <TableHead className="text-right">Valor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              : transferencias.length === 0
              ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                      Nenhuma transferência no período selecionado.
                    </TableCell>
                  </TableRow>
                )
              : transferencias.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>{fmtDate(t.dtMovimento)}</TableCell>
                    <TableCell>
                      <Badge variant={t.debitoCredito === 'C' ? 'default' : 'secondary'}>
                        {t.debitoCredito === 'C' ? 'Crédito' : 'Débito'}
                      </Badge>
                    </TableCell>
                    <TableCell>{t.caixaId}</TableCell>
                    <TableCell className="max-w-xs truncate">{t.descricao}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {t.historico ?? '—'}
                    </TableCell>
                    <TableCell
                      className={`text-right font-medium ${
                        t.debitoCredito === 'C' ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {t.debitoCredito === 'C' ? '+' : '-'}{fmt(t.valor)}
                    </TableCell>
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </div>

      <TransferenciaDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleRealizar}
        isLoading={isRealizando}
      />
    </div>
  );
}
